import classNames from 'classnames';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {Tracker, TrackImpression} from '@udemy/event-tracking';
import {LocalizedHtml, useFormatNumber, useI18n} from '@udemy/i18n';
import {Button, Picture} from '@udemy/react-core-components';
import {tokens} from '@udemy/styles';
import {
    PlanPrice,
    SubscriptionCTAClickEvent,
    SubscriptionOfferPresented,
    usePersonalPlanCollectionStats,
} from '@udemy/subscription-browse';
import {
    SubscriptionPlanProductType,
    SubscriptionPlansProvider,
    type SubscriptionPlansQueryVariables,
    useConsumerSubscriptionPlan,
} from '@udemy/subscription-graphql';
import {useFormatCurrency} from '@udemy/ud-data';

import styles from './pp-banner.module.less';

const PERSONAL_PLAN_LANDING_PAGE_URL = '/personal-plan/';

const PP_BANNER_UI_REGION = 'lihp_pp_module';

const IMAGE_BASE_URL =
    'https://frontends-dev.udemycdn.com/staticx/udemy/images/logged-in-home-page';

const MOBILE_MEDIA = `(max-width: ${tokens['breakpoint-sm-max']})`;

const BANNER_VARIANTS = {
    bannerVariantA: {
        variantClassName: styles['variant-a'],
        image: {
            src: `${IMAGE_BASE_URL}/variantA-desktop.png`,
            width: 537,
            height: 302,
        },
        mobileSource: {
            srcSet: `${IMAGE_BASE_URL}/variantA-mobile.png`,
            width: 243,
            height: 61,
            media: MOBILE_MEDIA,
        },
    },
    bannerVariantB: {
        variantClassName: styles['variant-b'],
        image: {
            src: `${IMAGE_BASE_URL}/variantB-desktop.png`,
            width: 569,
            height: 322,
        },
        mobileSource: {
            srcSet: `${IMAGE_BASE_URL}/variantB-mobile.png`,
            width: 59,
            height: 60,
            media: MOBILE_MEDIA,
        },
    },
};

export type PpBannerVariant = keyof typeof BANNER_VARIANTS;

export interface PpBannerProps {
    className?: string;
    variant?: PpBannerVariant;
}

const PpBannerContent = ({className, variant = 'bannerVariantA'}: PpBannerProps) => {
    const {gettext, interpolate} = useI18n();
    const {formatNumber} = useFormatNumber();
    const {formatCurrency} = useFormatCurrency();
    const {variantClassName, image, mobileSource} = BANNER_VARIANTS[variant];
    const consumerSubscriptionPlan = useConsumerSubscriptionPlan();
    const plan =
        consumerSubscriptionPlan &&
        !consumerSubscriptionPlan.isLoading &&
        'defaultPlanOption' in consumerSubscriptionPlan
            ? consumerSubscriptionPlan
            : undefined;

    const collectionStats = usePersonalPlanCollectionStats();
    const courseCount =
        collectionStats && !collectionStats.isLoading && 'numberOfCourses' in collectionStats
            ? collectionStats.numberOfCourses
            : undefined;
    const formattedMonthlyPrice =
        plan?.defaultPlanOption?.price?.monthly != null ? plan.formattedMonthlyAmount : undefined;

    const [hasBeenSeen, setHasBeenSeen] = useState(false);
    const offerPresentedSentRef = useRef(false);
    const handleImpression = useCallback(() => setHasBeenSeen(true), []);

    useEffect(() => {
        if (!hasBeenSeen || offerPresentedSentRef.current || !plan) {
            return;
        }
        offerPresentedSentRef.current = true;
        Tracker.publishEvent(
            new SubscriptionOfferPresented({
                subscriptionPlan: plan,
                productType: SubscriptionPlanProductType.Consumersubscription,
                countryCode: plan.defaultPlanOption.price?.currency ?? 'USD',
                uiRegion: PP_BANNER_UI_REGION,
            }),
        );
    }, [hasBeenSeen, plan]);

    const handleCtaClick = useCallback(() => {
        if (!plan) {
            return;
        }
        Tracker.publishEvent(
            new SubscriptionCTAClickEvent(
                plan.defaultPlanOption.id,
                plan.productType,
                plan.trialPeriodDays ? 'trial' : 'paid',
                PP_BANNER_UI_REGION,
                null,
                undefined,
                plan.planPriceOptionIds,
            ),
        );
    }, [plan]);

    return (
        <TrackImpression trackFunc={handleImpression} visibilityThreshold={0}>
            <section
                className={classNames(styles.banner, variantClassName, className)}
                data-purpose="pp-banner"
            >
                <div className={styles.content}>
                    <h2 className={classNames('ud-heading-xxl', styles.title)}>
                        {gettext('One Personal Plan subscription.')}{' '}
                        <br className={styles['title-break']} />
                        {gettext('Every skill you need.')}
                    </h2>
                    <p className={classNames('ud-text-sm', styles.description)}>
                        {courseCount == null
                            ? gettext('Access to top-rated courses in tech, business, and more.')
                            : interpolate(
                                  gettext(
                                      'Access to %(courseCount)s+ top-rated courses in tech, business, and more.',
                                  ),
                                  {courseCount: formatNumber(courseCount)},
                                  true,
                              )}
                    </p>
                    <Button
                        componentClass="a"
                        href={PERSONAL_PLAN_LANDING_PAGE_URL}
                        udStyle="white-solid"
                        className={styles.cta}
                        onClick={handleCtaClick}
                    >
                        {gettext('Learn more')}
                    </Button>
                    {formattedMonthlyPrice && (
                        <div className={classNames('ud-text-xs', styles.price)}>
                            <LocalizedHtml
                                html={gettext(
                                    'Starting at <span class="price">%(price)s</span>/month',
                                )}
                                interpolate={{
                                    price: (
                                        <PlanPrice
                                            listPriceText={
                                                plan?.selectedDiscount
                                                    ?.subscriptionNonDiscountMonthlyPrice
                                                    ? (formatCurrency(
                                                          plan.selectedDiscount
                                                              .subscriptionNonDiscountMonthlyPrice,
                                                      ) as string)
                                                    : formattedMonthlyPrice
                                            }
                                            discountPriceText={
                                                plan?.selectedDiscount
                                                    ?.subscriptionDiscountMonthlyPrice
                                                    ? (formatCurrency(
                                                          plan.selectedDiscount
                                                              .subscriptionDiscountMonthlyPrice,
                                                      ) as string)
                                                    : undefined
                                            }
                                        />
                                    ),
                                }}
                            />
                        </div>
                    )}
                </div>
                <Picture
                    {...image}
                    sources={[mobileSource]}
                    alt=""
                    className={styles['image-wrapper']}
                    imgClassName={styles.image}
                />
            </section>
        </TrackImpression>
    );
};

const PLAN_QUERY_VARIABLES: SubscriptionPlansQueryVariables[] = [
    {filters: {productType: SubscriptionPlanProductType.Consumersubscription}},
];

export const PpBanner = (props: PpBannerProps) => (
    <SubscriptionPlansProvider subscriptionPlanQueryVariables={PLAN_QUERY_VARIABLES}>
        <PpBannerContent {...props} />
    </SubscriptionPlansProvider>
);
